import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import store from 'store'

import Button from 'components/UI/Button'
import { BACKEND_REGISTRATION_CODES } from 'constants/register'

// Adjust path for actions
import { activate, sendConfirmation, setStep } from 'routing/fetchingfunction/user/user.action'

// Adjust paths for assets
import confirmEmail from 'assets/image/confirm-email.svg'
import emailConfirmed from 'assets/image/email-confirmed.svg'
import { TUTOR_ROLE } from 'constants'
import { selectEmail } from 'routing/fetchingfunction/user/user.reducer'

function ConfirmEmail() {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { uid, token: pathToken } = useParams()
  const [counter, setCounter] = useState(30) // For resend button cooldown
  const [counterRedirect, setCounterRedirect] = useState(5) // For redirect button cooldown
  // Select relevant data from Redux state
  const { userData, isEmailConfirmed, errors, role } = useSelector((state) => state.user)
  // const selectEmail = (state) => state.user.userData.email
  const selectedEmail = useSelector(selectEmail)
  const activationError = errors?.activation // Get activation error
  const FRONTEND_BASE_URL = (
    process.env.REACT_APP_FRONTEND_BASE_URL || window.location.origin
  ).replace(/\/+$/, '')

  const redirectIntervalRef = useRef(null)
  const redirectTimeoutRef = useRef(null)

  // State to track if we need to redirect after activation
  const [shouldRedirect, setShouldRedirect] = useState(false)

  const [isActivating, setIsActivating] = useState(false)
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false)
  // Create local confirmed state to persist through refreshes
  const [localConfirmed, setLocalConfirmed] = useState(false)

  const isactivate = location.pathname.includes('user/activate')

  const handleConfirmation = () => {
    setIsSendingConfirmation(true)
    dispatch(sendConfirmation({ email: selectedEmail }))
      .unwrap()
      .then(() => {
        setCounter(30) // Reset counter on resend
      })
      .catch((error) => console.log(error))
      .finally(() => setIsSendingConfirmation(false))
  }
  console.log(isEmailConfirmed, activationError, localConfirmed)

  // Effect to sync email confirmed state with localStorage on mount
  useEffect(() => {
    // Check local storage first
    const storedConfirmed = sessionStorage.getItem('email_confirmed') === 'true'
    if (storedConfirmed) {
      setLocalConfirmed(true)
      console.log('[ConfirmEmail] Email confirmed state loaded from sessionStorage')
    }
    // If Redux has confirmation, set it locally and in storage
    if (isEmailConfirmed && !storedConfirmed) {
      setLocalConfirmed(true)
      sessionStorage.setItem('email_confirmed', 'true')
      console.log('[ConfirmEmail] Email confirmed state set from Redux to localStorage')
    }
  }, [isEmailConfirmed])

  // Extract role from path if possible (e.g., /student/register/email-verification or /tutor/register/email-verification)
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    if (pathParts.length > 0) {
      const pathRole = pathParts[0]
      if (pathRole === 'student' || pathRole === 'tutor') {
        // Set the role in localStorage and Redux if it's not already set
        if (!store.get('user_role') || store.get('user_role') !== pathRole) {
          console.log(`[ConfirmEmail] Setting role from URL path: ${pathRole}`)
          store.set('user_role', pathRole)
        }
      }
    }
  }, [location.pathname])

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)

    if (pathParts.length > 0) {
      const pathRole = pathParts[0]
      if ((pathRole === 'student' || pathRole === 'tutor') && !isEmailConfirmed) {
        const interval = setInterval(() => {
          setCounter((prev) => (prev > 0 ? prev - 1 : 0))
        }, 1000)

        return () => clearInterval(interval)
      }
    }
  }, [activationError])

  useEffect(() => {
    if (counterRedirect === 0 && isEmailConfirmed) {
      handleContinue()
    }
  }, [counterRedirect, isEmailConfirmed])

  // Handler for the manual "Continue" button - only active if email is confirmed
  const getNextStep = () => {
    // Assuming student and tutor have PROFILE_UPDATE as the next step after email verification for now.
    // Adjust if TUTOR_TEST should come immediately after email for tutors.
    return BACKEND_REGISTRATION_CODES.PROFILE_UPDATE
  }

  // State to track if we need to navigate after continuing
  const [continueToNextStep, setContinueToNextStep] = useState(false)
  console.log(activationError, 'Activation Error:')
  const handleContinue = () => {
    if (isEmailConfirmed && activationError) {
      navigate('/login')
      return
    }
    if (isEmailConfirmed || localConfirmed) {
      const nextStep = getNextStep()
      console.log(
        '[ConfirmEmail] Continue button clicked, proceeding to step:',
        nextStep,
        'Role:',
        role
      )

      // First update the step in Redux and localStorage
      dispatch(
        setStep({
          step: nextStep,
          skipBackendUpdate: false, // Ensure backend gets updated
        })
      )
      store.set('register_step', nextStep)
      localStorage.setItem('register_step', nextStep)
      sessionStorage.removeItem('email_confirmed') // Clear the email confirmed flag on continue

      // Set flag to navigate in separate effect
      setContinueToNextStep(true)
    } else {
      navigate('/login')

      console.log('ConfirmEmail: Email not yet confirmed, cannot proceed')
    }
  }

  // Effect for handling Continue navigation
  useEffect(() => {
    if (continueToNextStep) {
      console.log(
        '[ConfirmEmail] Navigating to profile-update page after Continue button. Role:',
        role
      )

      // Force a small delay to ensure all state is updated
      setTimeout(() => {
        // Get current role - check Redux state, localStorage, or use default
        const currentRole = store.get('user_role') || role || 'student'
        console.log(`[ConfirmEmail] Using role for navigation: ${currentRole}`)

        // Use window.location for a clean navigation
        // window.location.href = `${FRONTEND_BASE_URL}/${currentRole}/register/profile-update`
        navigate(`/${currentRole}/register/profile-update`)
      }, 300)

      // Reset the flag
      setContinueToNextStep(false)
    }
  }, [continueToNextStep, role, FRONTEND_BASE_URL])

  // Effect to handle tokens if they're in the URL (for manual verification)
  useEffect(() => {
    console.log('[ConfirmEmail ActivationEffect] Running. Deps:', {
      pathToken: !!pathToken,
      uid: !!uid,
      isEmailConfirmed,
      localConfirmed,
      isActivating,
    })

    // Check if there are token parameters in the URL path
    if (pathToken && uid && !isEmailConfirmed && !localConfirmed && !isActivating) {
      // Extract role from path
      const pathParts = location.pathname.split('/').filter(Boolean)
      const pathRole =
        pathParts.length > 0 && (pathParts[0] === 'student' || pathParts[0] === 'tutor')
          ? pathParts[0]
          : store.get('user_role') || 'student'

      // Ensure the role is set in localStorage before activation
      store.set('user_role', pathRole)
      console.log(`[ConfirmEmail ActivationEffect] Setting role before activation: ${pathRole}`)

      console.log(
        '[ConfirmEmail ActivationEffect] Condition MET: Found tokens, not confirmed, not activating. Dispatching activate.'
      )
      setIsActivating(true)
      // Use uid from path as uid64, and pathToken as token for the activate action
      dispatch(
        activate({
          token: pathToken,
          uid64: uid,
          role: pathRole, // Pass the role explicitly to the activate action
        })
      )
        .unwrap() // unwrap to handle promise resolution/rejection here
        .then(() => {
          console.log('[ConfirmEmail ActivationEffect] Activation successful, marking as confirmed')
          setLocalConfirmed(true)
          sessionStorage.setItem('email_confirmed', 'true')
          setShouldRedirect(true)
        })
        .catch((err) => {
          console.error('[ConfirmEmail ActivationEffect] Activation dispatch failed:', err)
          // Error is already set in Redux state by activate.rejected

          // Check if the error indicates the user is already active
          if (
            err?.message?.toLowerCase?.().includes('already active') ||
            err?.message?.toLowerCase?.().includes('already activated')
          ) {
            console.log('[ConfirmEmail] Error indicates already active, marking as confirmed')
            setLocalConfirmed(true)
            sessionStorage.setItem('email_confirmed', 'true')
          }
        })
        .finally(() => {
          setIsActivating(false)
          console.log(
            '[ConfirmEmail ActivationEffect] Activation attempt finished (finally block).'
          )
        })
    } else {
      console.log(
        '[ConfirmEmail ActivationEffect] Condition NOT MET or already activating/confirmed.'
      )
    }
  }, [dispatch, uid, pathToken, isEmailConfirmed, localConfirmed, isActivating, location.pathname])

  // Separate effect for navigation after activation
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const pathRole =
      pathParts.length > 0 && (pathParts[0] === 'student' || pathParts[0] === 'tutor')
        ? pathParts[0]
        : store.get('user_role') || 'student'

    if (!shouldRedirect && isActivating) return
    redirectTimeoutRef.current = setTimeout(() => {
      console.log(
        `[ConfirmEmail] Performing redirect to email verification page for role---------------------: ${pathRole}`
      )

      store.set('register_step', 'email_verification')
      localStorage.setItem('register_step', 'email_verification')
      sessionStorage.setItem('email_confirmed', 'true')

      console.log('[ConfirmEmail] Current state before redirect:', {
        isEmailConfirmed,
        localConfirmed,
        role: pathRole,
        step: 'email_verification',
        tokens: {
          accessInLocalStorage: !!localStorage.getItem('access_token'),
          accessInStoreJs: !!store.get('access_token'),
        },
      })
      redirectIntervalRef.current = setInterval(() => {
        setCounterRedirect((prev) => {
          if (prev <= 1) {
            clearInterval(redirectIntervalRef.current)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 300)

    return () => {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current)
        redirectIntervalRef.current = null
      }

      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
    }
  }, [shouldRedirect, isActivating, location.pathname, isEmailConfirmed, localConfirmed])

  console.log(shouldRedirect, counterRedirect)
  // If we're on the email verification page with no tokens but email is confirmed,
  // this means we've been redirected back after successful activation
  useEffect(() => {
    if (isEmailConfirmed || localConfirmed) {
      console.log('[ConfirmEmail] Email is confirmed. Setting step in Redux and localStorage')

      // Update the step in Redux and localStorage
      dispatch(
        setStep({
          step: 'email_verification',
          skipBackendUpdate: true,
        })
      )
      store.set('register_step', 'email_verification')
      localStorage.setItem('register_step', 'email_verification')
    }
  }, [isEmailConfirmed, localConfirmed, dispatch])

  console.log(
    !isEmailConfirmed && !activationError,
    isEmailConfirmed,
    !activationError,
    'Render:',
    userData
  )
  // --- Render Logic ---
  return (
    <div className="max-w-[800px] px-8 py-[53px] mx-auto w-full flex flex-col items-center justify-start !text-white-50">
      {/* Title changes based on confirmation status */}
      <h3 className="lg:text-5xl font-bold !mb-3 text-3xl">
        {userData?.isLoading
          ? 'Loading...'
          : !isEmailConfirmed && !isactivate
            ? 'Confirm Email'
            : 'Email Confirmed'}
      </h3>

      {/* Text content varies based on confirmation status */}
      {!isEmailConfirmed && !userData?.isLoading ? (
        <p className="text-center mb-16 text-xl">
          Please check your inbox. We have sent a verification link to{' '}
          {selectedEmail || 'your email address'}
        </p>
      ) : isEmailConfirmed && activationError ? (
        <>
          <p className="text-center text-xl !mb-3">
            {typeof activationError === 'string'
              ? activationError
              : JSON.stringify(activationError)}
          </p>
        </>
      ) : userData.isLoading ? (
        <>
          <p className="text-center text-xl !mb-3">Verifying </p>
        </>
      ) : (
        <>
          <p className="text-center text-xl !mb-3">
            Your email has been verified
            <br /> Thank you!
          </p>
          <p className="text-center text-xl !mb-3 text-white">
            You will be redirected to the next page in {`{${counterRedirect}}`}...
          </p>
        </>
      )}

      {/* Display error if not confirmed, not activating, and error exists */}
      {!isEmailConfirmed && activationError && !isActivating && (
        <p className="text-center mb-4 text-xl text-red-500">
          Activation failed:{' '}
          {typeof activationError === 'string' ? activationError : JSON.stringify(activationError)}
        </p>
      )}

      {/* {isEmailConfirmed && (
        <p className="text-center mb-4 text-xl !text-white-50">
          Email confirmed successfully! Click "Continue" to proceed.
        </p>
      )} */}
      {/* Image changes based on confirmation status */}
      <div>
        <img
          className="block"
          src={isEmailConfirmed ? emailConfirmed : confirmEmail}
          alt={isEmailConfirmed ? 'email confirmed illustration' : 'confirm email illustration'}
        />
      </div>

      {/* Button to proceed */}
      {isEmailConfirmed ? (
        <Button
          className="w-full !mt-5 font-semibold max-w-lg border !border-gray-500/30 shadow !text-white-50"
          onClick={handleContinue}
          disabled={!isEmailConfirmed}
          variant={isEmailConfirmed ? 'primary' : 'secondary'}
        >
          {isEmailConfirmed && activationError
            ? 'Continue to Login'
            : isEmailConfirmed && !activationError
              ? 'Continue Registration'
              : 'Confirm Email'}
        </Button>
      ) : (
        <>
          <p className="mt-4 text-base text-[#FDFDFD] font-semibold">Didn't receive the email?</p>
          <Button
            className={`w-full max-w-lg border !border-gray-500/30 shadow !text-white-50  ${counter > 0 || isSendingConfirmation ? '!bg-[#FEC8C8]' : '!bg-[#F79009]'} !mt-5 font-semibold`}
            onClick={handleConfirmation}
            disabled={counter > 0 || isSendingConfirmation}
            variant={isEmailConfirmed || counter === 0 ? 'primary' : 'secondary'}
          >
            Resend Email {counter > 0 && `(${counter}s)`}
          </Button>
        </>
      )}
    </div>
  )
}

export default ConfirmEmail
